import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { ConnectorView, mcpMarkFor } from './connectors.models';
import { OPTION_ROW_CLASS, OPTION_ROW_ERROR_CLASS } from './connectors.styles';

/**
 * One connector in the Connectors settings (Figma: 12866-30705, list row 12857-29314).
 *
 * 72px tall, vendor mark, name over an optional description, and on the right either a "Connect"
 * button or a "Connected" badge with a `...` menu (Figma: MCP - More Options 12866-30909) holding
 * re-authenticate and disconnect.
 *
 * Presentational: it reports intent and never touches the store, so the Connectors panel owns the
 * confirmation and the dispatching.
 */
@Component({
  selector: 'squadbox-connector-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkMenu,
    CdkMenuItem,
    CdkMenuTrigger,
    TranslateModule,
    fromSeriousSystem.ButtonDirective,
    fromSeriousSystem.UseMcpIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  template: `
    <div
      class="flex items-center gap-4 min-h-18 px-3 py-4 border-b border-neutral-200"
    >
      @if (mark(); as icon) {
        <svg
          [sdUseMcpIcon]="icon"
          class="size-8 shrink-0"
          aria-hidden="true"
        ></svg>
      } @else {
        <svg
          sdUseUIBasicIcon="share"
          class="size-8 shrink-0 text-neutral-500"
          aria-hidden="true"
        ></svg>
      }

      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <span class="typo-p3 leading-5 font-semibold text-neutral-900 truncate">
          {{ connector().displayName }}
        </span>
        <!-- The mobile variant carries the name alone (Figma 13029-34213 /
             13029-34204): at that width the row has no room for a second line
             beside the badge and the menu. -->
        @if (!isMobile() && description(); as text) {
          <span class="typo-caption leading-4 text-neutral-500 line-clamp-2">
            {{ text }}
          </span>
        }
      </div>

      <div class="flex items-center gap-2 shrink-0">
        @if (isLinked()) {
          @if (connector().status === 'connected') {
            <!-- Mobile drops the label and keeps the check alone, so the badge
                 becomes a 32px disc (Figma 13029-34204). The label still names
                 the state for screen readers at both widths. -->
            <span
              class="flex items-center gap-1 h-8 rounded-full bg-success-50 typo-caption leading-4 font-semibold text-success-500"
              [class.p-2]="isMobile()"
              [class.pl-3]="!isMobile()"
              [class.pr-4]="!isMobile()"
              [attr.aria-label]="
                isMobile() ? ('CONNECTORS.STATUS.CONNECTED' | translate) : null
              "
            >
              <svg
                sdUseUIBasicIcon="check"
                class="size-4 shrink-0"
                aria-hidden="true"
              ></svg>
              @if (!isMobile()) {
                {{ 'CONNECTORS.STATUS.CONNECTED' | translate }}
              }
            </span>
          } @else {
            <!--
              Not in the design: a link whose credential the provider no longer honours. It keeps the
              badge shape so the row does not jump, in the error palette because the person has to act
              — "Re-authenticate" in the same menu is the fix.
            -->
            <span
              class="flex items-center gap-1 h-8 rounded-full bg-error-50 typo-caption leading-4 font-semibold text-error-600"
              [class.p-2]="isMobile()"
              [class.pl-3]="!isMobile()"
              [class.pr-4]="!isMobile()"
              [attr.aria-label]="
                isMobile()
                  ? ('CONNECTORS.STATUS.NEEDS_RECONNECT' | translate)
                  : null
              "
            >
              <svg
                sdUseUIBasicIcon="info"
                class="size-4 shrink-0"
                aria-hidden="true"
              ></svg>
              @if (!isMobile()) {
                {{ 'CONNECTORS.STATUS.NEEDS_RECONNECT' | translate }}
              }
            </span>
          }

          <button
            type="button"
            [cdkMenuTriggerFor]="moreMenu"
            class="size-8 shrink-0 flex items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 focus:outline-hidden cursor-pointer disabled:cursor-not-allowed"
            [disabled]="isBusy()"
            [attr.aria-label]="
              'CONNECTORS.ACTIONS.MORE'
                | translate: { connector: connector().displayName }
            "
          >
            <svg class="size-5" sdUseUIBasicIcon="more-horizontal"></svg>
          </button>
        } @else {
          <button
            sdButton
            variant="outline"
            color="neutral"
            size="xs"
            class="shrink-0"
            (click)="connect.emit()"
          >
            {{ 'CONNECTORS.ACTIONS.CONNECT' | translate }}
          </button>
        }
      </div>
    </div>

    <ng-template #moreMenu>
      <div
        cdkMenu
        role="menu"
        class="flex flex-col gap-0.5 p-2 min-w-50 bg-shades-white border border-neutral-200 rounded-lg shadow-md"
      >
        <button
          cdkMenuItem
          type="button"
          [class]="optionRowClass"
          (click)="reauthenticate.emit()"
        >
          <svg
            sdUseUIBasicIcon="refresh"
            class="size-5 shrink-0"
            aria-hidden="true"
          ></svg>
          <span class="flex-1 truncate">
            {{ 'CONNECTORS.ACTIONS.REAUTHENTICATE' | translate }}
          </span>
        </button>

        <div class="p-2" aria-hidden="true">
          <div class="border-t border-neutral-200"></div>
        </div>

        <button
          cdkMenuItem
          type="button"
          [class]="optionRowErrorClass"
          (click)="disconnect.emit()"
        >
          <svg
            sdUseUIBasicIcon="delete"
            class="size-5 shrink-0"
            aria-hidden="true"
          ></svg>
          <span class="flex-1 truncate">
            {{ 'CONNECTORS.ACTIONS.DISCONNECT' | translate }}
          </span>
        </button>
      </div>
    </ng-template>
  `,
})
export class ConnectorListRowComponent {
  private readonly translateService = inject(TranslateService);

  protected readonly optionRowClass = OPTION_ROW_CLASS;
  protected readonly optionRowErrorClass = OPTION_ROW_ERROR_CLASS;

  public readonly connector = input.required<ConnectorView>();
  /** Draws the Mobile variant: no description line, and a check-only badge. */
  public readonly isMobile = input<boolean>(false);
  /** A disconnect is in flight for this row, so the menu should not take a second one. */
  public readonly isBusy = input<boolean>(false);

  public readonly connect = output<void>();
  public readonly reauthenticate = output<void>();
  public readonly disconnect = output<void>();

  /** `status` is null until the person links the provider; both linked states keep the `...` menu. */
  protected readonly isLinked = computed(
    () => this.connector().status !== null,
  );

  /** The vendor mark for this provider; see `mcpMarkFor` for why it is derived rather than sent. */
  protected readonly mark = computed<fromSeriousSystem.McpIconsType | null>(
    () => mcpMarkFor(this.connector().provider),
  );

  /**
   * Per-connector copy lives in the UI's translations rather than the catalog, so it is translated and
   * costs no API change. A catalog id renders no description line until copy exists for it — either
   * because the key is absent, in which case ngx-translate echoes the key back, or because it is still
   * the empty placeholder the translation files ship.
   */
  protected readonly description = computed(() => {
    const key = `CONNECTORS.DESCRIPTIONS.${this.connector().provider}`;
    const text = this.translateService.instant(key) as string;
    return !text || text === key ? '' : text;
  });
}
